<header
  class="h-14 bg-base-100 border-b border-base-300 px-4 lg:px-6 flex items-center justify-between sticky top-0 z-40 shadow-xs transition-all duration-300"
>
  <!-- Left: Sidebar Toggle + Page Title -->
  <div class="flex items-center gap-3">
    <!-- Desktop Sidebar Toggle -->
    <button
      type="button"
      class="hidden lg:flex daisy-btn daisy-btn-ghost daisy-btn-sm daisy-btn-square text-base-content hover:bg-base-200"
      (click)="toggleDesktopSidebar()"
      [title]="desktopCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      aria-label="Toggle sidebar collapse"
    >
      @if (!desktopCollapsed) {
        <svg
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      } @else {
        <svg
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 5l7 7-7 7"
          />
        </svg>
      }
    </button>

    <!-- Mobile Hamburger Toggle -->
    <button
      type="button"
      class="lg:hidden daisy-btn daisy-btn-ghost daisy-btn-sm daisy-btn-square text-base-content"
      (click)="toggleMobileSidebar()"
      aria-label="Toggle mobile menu"
    >
      <i class="fa-solid fa-bars text-lg"></i>
    </button>

    <!-- Page Title -->
    <div class="flex items-center gap-2">
      <h1
        class="text-base sm:text-lg font-bold text-base-content tracking-tight"
      >
        {{ currentTitle }}
      </h1>
    </div>
  </div>

  <!-- Right: Actions + User Profile Dropdown -->
  <div class="flex items-center gap-2 sm:gap-3">
    <!-- Theme Toggle -->
    @if (isThemeToggleAvailable) {
      <button
        type="button"
        (click)="toggleTheme()"
        class="daisy-btn daisy-btn-ghost daisy-btn-sm daisy-btn-circle text-base-content hover:bg-base-200"
        [attr.aria-label]="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
        [title]="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
      >
        @if (isDarkMode) {
          <i class="fa-solid fa-sun text-warning text-base"></i>
        } @else {
          <i class="fa-solid fa-moon text-base-content/70 text-base"></i>
        }
      </button>
    }

    <!-- Back to Site -->
    <a
      routerLink="/"
      class="hidden sm:inline-flex daisy-btn daisy-btn-ghost daisy-btn-sm gap-2 text-xs text-base-content/80 hover:text-base-content"
    >
      <i class="fa-solid fa-arrow-left"></i>
      <span>Back to Site</span>
    </a>

    <!-- Notification Bell -->
    <black-notification-bell></black-notification-bell>

    <!-- User Profile Dropdown -->
    <div class="daisy-dropdown daisy-dropdown-end">
      <label
        tabindex="0"
        class="daisy-btn daisy-btn-ghost daisy-btn-circle daisy-avatar daisy-placeholder cursor-pointer p-0.5 hover:ring-2 hover:ring-primary/40 transition-all"
      >
        <div
          class="bg-primary text-primary-content rounded-full w-9 h-9 flex items-center justify-center shadow-xs"
        >
          <ng-container
            *ngIf="
              getAvatarUrl(user?.profile?.personal?.avatar);
              else headerInitial
            "
          >
            <img
              [src]="getAvatarUrl(user?.profile?.personal?.avatar)"
              alt="User Avatar"
              class="w-full h-full object-cover rounded-full"
            />
          </ng-container>
          <ng-template #headerInitial>
            <span class="text-sm font-bold">{{ userInitial }}</span>
          </ng-template>
        </div>
      </label>

      <!-- Dropdown Menu -->
      <ul
        tabindex="0"
        class="daisy-dropdown-content daisy-menu p-2 shadow-2xl bg-base-100 rounded-2xl w-64 border border-base-300 mt-2 z-50 text-sm"
      >
        <!-- User Info Header -->
        <li class="px-3 py-2.5 border-b border-base-200">
          <div class="flex flex-col gap-0.5 p-0 hover:bg-transparent">
            <p class="font-semibold text-base-content truncate">
              {{ fullName }}
            </p>
            <div class="flex items-center gap-2 mt-0.5">
              @if (isInstructor) {
                <span class="daisy-badge daisy-badge-primary daisy-badge-sm"
                  >Instructor</span
                >
              } @else {
                <span class="daisy-badge daisy-badge-primary daisy-badge-sm"
                  >Student</span
                >
              }
            </div>
          </div>
        </li>

        <!-- Links -->
        <li class="mt-1">
          <a
            routerLink="/dashboard/overview"
            class="flex items-center gap-2.5 rounded-lg py-2"
          >
            <i class="fa-solid fa-gauge text-primary w-4 text-center"></i>
            <span>Overview</span>
          </a>
        </li>
        <li>
          <a
            routerLink="/dashboard/profile"
            class="flex items-center gap-2.5 rounded-lg py-2"
          >
            <i
              class="fa-solid fa-user text-base-content/70 w-4 text-center"
            ></i>
            <span>My Profile</span>
          </a>
        </li>

        @if (isInstructorOrAdmin && showTutorProfile) {
          <li>
            <a
              routerLink="/dashboard/profile/tutor-profile"
              class="flex items-center justify-between rounded-lg py-2"
            >
              <div class="flex items-center gap-2.5">
                <i
                  class="fa-solid fa-id-card text-base-content/70 w-4 text-center"
                ></i>
                <span>Tutor Profile</span>
              </div>
              @if (hasIncompleteTutorProfile) {
                <span class="flex h-2 w-2 relative">
                  <span
                    class="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"
                  ></span>
                  <span
                    class="relative inline-flex rounded-full h-2 w-2 bg-amber-500"
                  ></span>
                </span>
              }
            </a>
          </li>
        }

        <li>
          <a
            routerLink="/dashboard/subscriptions"
            class="flex items-center gap-2.5 rounded-lg py-2"
          >
            <i
              class="fa-solid fa-credit-card text-base-content/70 w-4 text-center"
            ></i>
            <span>Subscriptions</span>
          </a>
        </li>

        <!-- Back to Site (mobile dropdown link) -->
        <li class="sm:hidden">
          <a routerLink="/" class="flex items-center gap-2.5 rounded-lg py-2">
            <i
              class="fa-solid fa-globe text-base-content/70 w-4 text-center"
            ></i>
            <span>Back to Main Site</span>
          </a>
        </li>

        <div class="h-px bg-base-200 my-1"></div>

        <!-- Logout -->
        <li>
          <button
            (click)="logout()"
            class="flex items-center gap-2.5 rounded-lg py-2 text-error hover:bg-error/10"
          >
            <i class="fa-solid fa-arrow-right-from-bracket w-4 text-center"></i>
            <span>Sign Out</span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</header>
